import { supabase } from "@/lib/supabase";
import type { Database } from "@/integrations/supabase/types";
import { compressImageSafeCanvas } from "@/lib/image-compression";
import { EQUIPMENT_PROTOCOL_STORAGE_BUCKET, EQUIPMENT_RPC } from "@/types/equipment";
import { EquipmentApiError, equipmentErrorMessagePl, parseEquipmentErrorCode } from "@/lib/equipmentMessages";

type PublicFunctions = Database["public"]["Functions"];
type EquipmentRpcName = (typeof EQUIPMENT_RPC)[keyof typeof EQUIPMENT_RPC];

export function toEquipmentError(error: unknown, context: string): EquipmentApiError {
  const code = parseEquipmentErrorCode(error);
  console.error(`[equipmentApi:${context}]`, code, error);
  return new EquipmentApiError(code, equipmentErrorMessagePl(code));
}

export async function rpcEquipment<K extends EquipmentRpcName>(
  fn: K,
  args: PublicFunctions[K]["Args"],
  context: string,
): Promise<PublicFunctions[K]["Returns"]> {
  const { data, error } = await supabase.rpc(fn, args);
  if (error) throw toEquipmentError(error, context);
  if (data == null) throw new EquipmentApiError("EQUIPMENT_NOT_FOUND");
  return data as unknown as PublicFunctions[K]["Returns"];
}

const MAX_CONDITION_NOTES = 2000;
const MAX_PHOTOS = 5;
const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
const ALLOWED_PHOTO_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
]);

export function validateConditionNotes(notes: string | null | undefined): string | null {
  const trimmed = notes?.trim() ?? "";
  if (!trimmed) return null;
  if (trimmed.length > MAX_CONDITION_NOTES) {
    throw new EquipmentApiError("EQUIPMENT_NOTES_TOO_LONG");
  }
  return trimmed;
}

export function validateProtocolPhotos(files: File[]): File[] {
  if (files.length > MAX_PHOTOS) {
    throw new EquipmentApiError("EQUIPMENT_PHOTO_TOO_MANY");
  }
  for (const file of files) {
    const type = (file.type || "").toLowerCase();
    if (type && !ALLOWED_PHOTO_TYPES.has(type) && !type.startsWith("image/")) {
      throw new EquipmentApiError("EQUIPMENT_PHOTO_INVALID");
    }
    if (file.size > MAX_PHOTO_BYTES) {
      throw new EquipmentApiError("EQUIPMENT_PHOTO_TOO_LARGE");
    }
  }
  return files;
}

export async function uploadProtocolPhotos(
  orgId: string,
  protocolId: string,
  files: File[],
): Promise<string[]> {
  validateProtocolPhotos(files);
  const paths: string[] = [];
  for (const file of files) {
    const compressed = await compressImageSafeCanvas(file, { maxWidthOrHeight: 1280, initialQuality: 0.7 });
    const path = `${orgId}/${protocolId}/${Date.now()}_${Math.random().toString(36).slice(2, 10)}.jpg`;
    const { error } = await supabase.storage
      .from(EQUIPMENT_PROTOCOL_STORAGE_BUCKET)
      .upload(path, compressed, { cacheControl: "3600", contentType: "image/jpeg", upsert: false });
    if (error) {
      console.error("[equipmentApi:uploadProtocolPhotos]", error);
      throw new EquipmentApiError("EQUIPMENT_RPC_FAILED", "Nie udało się przesłać zdjęcia protokołu.");
    }
    paths.push(path);
  }
  return paths;
}

export async function resolveEquipmentPhotoUrl(pathOrUrl: string): Promise<string | null> {
  if (!pathOrUrl) return null;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const { data, error } = await supabase.storage
    .from(EQUIPMENT_PROTOCOL_STORAGE_BUCKET)
    .createSignedUrl(pathOrUrl, 3600);
  if (error) {
    console.error("[equipmentApi:resolveEquipmentPhotoUrl]", error);
    return null;
  }
  return data?.signedUrl ?? null;
}
